import React, { useEffect, useState } from "react";
import {
  Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, TextField, InputAdornment,
  LinearProgress, Chip, Box, IconButton, Tooltip
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import RefreshIcon from "@material-ui/icons/Refresh";
import WarningIcon from "@material-ui/icons/Warning";
import { makeStyles } from "@material-ui/core/styles";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(3) },
  title: { marginBottom: theme.spacing(2), fontWeight: 700 },
  searchBox: { marginBottom: theme.spacing(2), maxWidth: 400 },
  tableHeader: { backgroundColor: "#f5f5f5", fontWeight: 700 },
  progressNormal: { backgroundColor: "#e8f5e9", "& .MuiLinearProgress-bar": { backgroundColor: "#43a047" } },
  progressRed: { backgroundColor: "#ffebee", "& .MuiLinearProgress-bar": { backgroundColor: "#e53935" } },
  chipNormal: { backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 },
  chipRed: { backgroundColor: "#ffebee", color: "#c62828", fontWeight: 600 },
  rowRed: { backgroundColor: "#fff5f5", "&:hover": { backgroundColor: "#ffe8e8" } },
  rowNormal: { "&:hover": { backgroundColor: "#f9f9f9" } },
  summary: {
    display: "flex", gap: theme.spacing(3), marginBottom: theme.spacing(3),
    flexWrap: "wrap"
  },
  summaryCard: {
    padding: theme.spacing(2), borderRadius: 8, minWidth: 180,
    background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.1)"
  },
  alertBanner: {
    padding: "10px 16px", borderRadius: 6, marginBottom: 16,
    background: "#ffebee", border: "1px solid #ef9a9a",
    display: "flex", alignItems: "center", gap: 8
  }
}));

const AiUsage = () => {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies-ai-usage");
      const sorted = [...data].sort((a, b) => {
        if (a.tokenLimit === 0 && b.tokenLimit === 0) return 0;
        if (a.tokenLimit === 0) return 1;
        if (b.tokenLimit === 0) return -1;
        return b.pct - a.pct;
      });
      setRows(sorted);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r =>
    r.name.toLowerCase().includes(filter.toLowerCase()) ||
    r.email.toLowerCase().includes(filter.toLowerCase())
  );

  const isRed = pct => pct >= 90;

  const totalCost = rows.reduce((acc, r) => acc + (r.usedBRL || 0), 0).toFixed(2);
  const withAi = rows.filter(r => r.tokenLimit > 0).length;
  const atRisk = rows.filter(r => isRed(r.pct)).length;

  return (
    <div className={classes.root}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h5" className={classes.title}>
          Consumo de IA por Empresa
        </Typography>
        <Tooltip title="Atualizar">
          <IconButton onClick={load} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {atRisk > 0 && (
        <div className={classes.alertBanner}>
          <WarningIcon style={{ color: "#c62828" }} />
          <div>
            <strong style={{ color: "#c62828" }}>
              {atRisk} {atRisk === 1 ? "empresa atingiu" : "empresas atingiram"} 90% do limite de tokens.
            </strong>
            <span style={{ color: "#666", marginLeft: 8 }}>
              Entre em contato para oferecer upgrade antes que a IA pare.
            </span>
          </div>
        </div>
      )}

      <div className={classes.summary}>
        <Paper className={classes.summaryCard}>
          <Typography variant="caption" color="textSecondary">Empresas com IA</Typography>
          <Typography variant="h5" style={{ fontWeight: 700 }}>{withAi}</Typography>
        </Paper>
        <Paper className={classes.summaryCard}>
          <Typography variant="caption" color="textSecondary">Custo total do mes</Typography>
          <Typography variant="h5" style={{ fontWeight: 700, color: "#1976d2" }}>R$ {totalCost}</Typography>
        </Paper>
        <Paper className={classes.summaryCard}>
          <Typography variant="caption" color="textSecondary">Acima de 90% do limite</Typography>
          <Typography variant="h5" style={{ fontWeight: 700, color: atRisk > 0 ? "#e53935" : "#43a047" }}>{atRisk}</Typography>
        </Paper>
      </div>

      <TextField
        className={classes.searchBox}
        variant="outlined"
        size="small"
        placeholder="Filtrar por empresa ou email..."
        value={filter}
        onChange={e => setFilter(e.target.value)}
        fullWidth
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
        }}
      />

      {loading && <LinearProgress style={{ marginBottom: 8 }} />}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHeader}>Empresa</TableCell>
              <TableCell className={classes.tableHeader}>Plano</TableCell>
              <TableCell className={classes.tableHeader} align="right">Limite (tokens)</TableCell>
              <TableCell className={classes.tableHeader} align="right">Limite (R$)</TableCell>
              <TableCell className={classes.tableHeader} align="right">Usado (tokens)</TableCell>
              <TableCell className={classes.tableHeader} align="right">Usado (R$)</TableCell>
              <TableCell className={classes.tableHeader} align="center">Consumo</TableCell>
              <TableCell className={classes.tableHeader} align="center">Reset em</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(row => {
              const red = isRed(row.pct) && row.tokenLimit > 0;
              const resetDate = row.resetDate ? new Date(row.resetDate).toLocaleDateString("pt-BR") : "-";
              return (
                <TableRow key={row.id} className={red ? classes.rowRed : classes.rowNormal}>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                      {red && <WarningIcon style={{ color: "#e53935", fontSize: 16 }} />}
                      <div>
                        <div style={{ fontWeight: 600, color: red ? "#c62828" : undefined }}>{row.name}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>{row.email}</div>
                      </div>
                    </Box>
                  </TableCell>
                  <TableCell>{row.planName}</TableCell>
                  <TableCell align="right">
                    {row.tokenLimit > 0 ? row.tokenLimit.toLocaleString("pt-BR") : <span style={{ color: "#aaa" }}>-</span>}
                  </TableCell>
                  <TableCell align="right">
                    {row.tokenLimit > 0 ? ("R$ " + row.limitBRL.toFixed(2)) : <span style={{ color: "#aaa" }}>-</span>}
                  </TableCell>
                  <TableCell align="right" style={{ fontWeight: red ? 700 : 400, color: red ? "#c62828" : undefined }}>
                    {row.tokensUsed.toLocaleString("pt-BR")}
                  </TableCell>
                  <TableCell align="right" style={{ fontWeight: red ? 700 : 400, color: red ? "#c62828" : "#1976d2" }}>
                    {"R$ " + row.usedBRL.toFixed(2)}
                  </TableCell>
                  <TableCell align="center" style={{ minWidth: 160 }}>
                    {row.tokenLimit > 0 ? (
                      <Box display="flex" alignItems="center">
                        <Box flex={1} mr={1}>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(row.pct, 100)}
                            className={red ? classes.progressRed : classes.progressNormal}
                          />
                        </Box>
                        <Chip
                          label={row.pct + "%"}
                          size="small"
                          className={red ? classes.chipRed : classes.chipNormal}
                        />
                      </Box>
                    ) : (
                      <span style={{ color: "#aaa", fontSize: 12 }}>Chave propria</span>
                    )}
                  </TableCell>
                  <TableCell align="center" style={{ fontSize: 12 }}>{resetDate}</TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" style={{ color: "#aaa", padding: 32 }}>
                  Nenhuma empresa encontrada
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default AiUsage;
